import { Injectable, effect, inject, signal } from '@angular/core';
import { InMemoryCacheService } from '../cache/in-memory-cache.service';
import { PermissionKey, PermissionScope } from '../permissions/permission.types';
import { OrganizationRolesService } from 'src/app/pages/organization-settings/services/organization-roles.service';
import { RolesPolicyResponse, RolesPolicyScope } from 'src/app/pages/organization-settings/models/roles-policy.model';
import { OrganizationSettingsService } from 'src/app/pages/organization-settings/services/organization-settings.service';

interface PermissionMatrixEntry {
  scope: PermissionScope;
  effectiveAllowedRoles: string[];
  roles: string[];
  description?: string;
}

interface RuntimePermissionDefinition {
  scope: PermissionScope;
  effectiveAllowedRoles: string[];
  description?: string;
}

interface PermissionMatrixSnapshot {
  orgId: number;
  permissionMatrixVersion: string | null;
  orgAdminsImplicitProjectAccess: boolean;
  orgAdminsImplicitCardAccess: boolean;
  permissions: Map<PermissionKey, PermissionMatrixEntry>;
}

interface PermissionMatrixLoadOptions {
  requireFresh?: boolean;
  maxAgeMs?: number;
}

interface PermissionMatrixCacheMetadata {
  orgId: number;
  version: string;
  fetchedAt: number;
  responseHash: string;
  storageKey: string;
}

interface PermissionMatrixCacheRecord {
  orgId: number;
  version: string;
  fetchedAt: number;
  response: RolesPolicyResponse;
}

interface ApplyResponseOptions {
  forceApply?: boolean;
  fetchedAt?: number;
  persistToStorage?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionMatrixStore {
  private readonly refreshIntervalMs = 5 * 60 * 1000;
  private readonly storageKeyPrefix = 'permission-matrix:org:';
  private readonly storageMetaSuffix = ':meta';
  private readonly cache = new Map<number, PermissionMatrixSnapshot>();
  private readonly responseHashes = new Map<number, string>();
  private readonly serverVersions = new Map<number, string | null>();
  private readonly lastFetchedAt = new Map<number, number>();
  private readonly loading = new Set<number>();
  private readonly inflightLoads = new Map<number, Promise<void>>();
  private readonly sessionFetched = new Set<number>();
  private readonly _version = signal(0);
  private readonly _currentOrgId = signal<number | null>(null);

  public readonly currentOrgId = this._currentOrgId.asReadonly();
  public readonly version = this._version.asReadonly();

  private readonly orgRolesService = inject(OrganizationRolesService);

  public constructor(
    private readonly orgSettingsService: OrganizationSettingsService,
    private readonly inMemoryCache: InMemoryCacheService
  ) {
    this.startBackgroundRevalidation();

    effect(() => {
      const orgId = this.resolveCurrentOrgId();
      this._currentOrgId.set(orgId);

      if (!orgId) {
        return;
      }

      void this.ensureLoaded(orgId);
    }, { allowSignalWrites: true });
  }

  public getEffectiveAllowedRoles(permission: PermissionKey, scope: PermissionScope): string[] | null {
    const snapshot = this.getSnapshot();
    const entry = snapshot?.permissions.get(permission);

    if (!entry || entry.scope !== scope) {
      return null;
    }

    return entry.effectiveAllowedRoles ?? null;
  }

  public getPermissionDefinition(permission: PermissionKey): RuntimePermissionDefinition | null {
    const snapshot = this.getSnapshot();
    const entry = snapshot?.permissions.get(permission);
    if (!entry) {
      return null;
    }

    return {
      scope: entry.scope,
      effectiveAllowedRoles: entry.effectiveAllowedRoles,
      description: entry.description
    };
  }

  public async preloadCurrentOrg(options: PermissionMatrixLoadOptions = {}): Promise<void> {
    const orgId = await this.resolveCurrentOrgIdWithRetry();
    if (!orgId) {
      return;
    }

    if (this._currentOrgId() !== orgId) {
      this._currentOrgId.set(orgId);
    }

    await this.ensureLoaded(orgId, options);
  }

  public getOrgAdminsImplicitProjectAccess(): boolean {
    const snapshot = this.getSnapshot();
    return snapshot?.orgAdminsImplicitProjectAccess ?? false;
  }

  public getOrgAdminsImplicitCardAccess(): boolean {
    const snapshot = this.getSnapshot();
    return snapshot?.orgAdminsImplicitCardAccess ?? false;
  }

  public setFromResponse(orgId: number, response: RolesPolicyResponse): void {
    this.applyResponse(orgId, response, { forceApply: true });
  }

  public clearPersistedCache(): void {
    if (!this.canUseStorage()) {
      return;
    }

    const keysToRemove: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(this.storageKeyPrefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => this.removeStorageEntry(key));
    this.cache.clear();
    this.responseHashes.clear();
    this.serverVersions.clear();
    this.lastFetchedAt.clear();
    this.sessionFetched.clear();
    this.bumpVersion();
  }

  private async ensureLoaded(orgId: number, options: PermissionMatrixLoadOptions = {}): Promise<void> {
    if (this.loading.has(orgId)) {
      const inflightLoad = this.inflightLoads.get(orgId);
      if (inflightLoad) {
        await inflightLoad;
      }
      return;
    }

    if (!this.cache.has(orgId)) {
      this.hydrateFromStorage(orgId);
    }

    const hasCachedSnapshot = this.cache.has(orgId);
    const hasSessionFetch = this.sessionFetched.has(orgId);
    const shouldFetch = !hasCachedSnapshot || !hasSessionFetch || this.shouldRevalidate(orgId, options.maxAgeMs);
    if (!shouldFetch) {
      return;
    }

    const loadPromise = this.loadPolicyForOrg(orgId);
    this.inflightLoads.set(orgId, loadPromise);

    if (hasCachedSnapshot && !options.requireFresh) {
      void loadPromise;
      return;
    }

    await loadPromise;
  }

  private async loadPolicyForOrg(orgId: number): Promise<void> {
    this.loading.add(orgId);
    try {
      const response = await this.orgRolesService.getRolesPolicy(orgId);
      if (response) {
        this.applyResponse(orgId, response);
        this.sessionFetched.add(orgId);
      }
    } finally {
      this.loading.delete(orgId);
      this.inflightLoads.delete(orgId);
    }
  }

  private startBackgroundRevalidation(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.setInterval(() => {
      this.revalidateCurrentOrg();
    }, this.refreshIntervalMs);

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          this.revalidateCurrentOrg();
        }
      });
    }
  }

  private revalidateCurrentOrg(): void {
    const orgId = this.getCurrentOrgIdForLookup();
    if (!orgId) {
      return;
    }

    void this.ensureLoaded(orgId);
  }

  private async resolveCurrentOrgIdWithRetry(): Promise<number | null> {
    const orgIdFromConfig = this.resolveCurrentOrgId();
    if (orgIdFromConfig) {
      return orgIdFromConfig;
    }

    const startedAt = Date.now();
    const timeoutMs = 1500;
    const intervalMs = 50;

    while (Date.now() - startedAt < timeoutMs) {
      const resolvedOrgId = this.resolveCurrentOrgId();
      if (resolvedOrgId) {
        return resolvedOrgId;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    return null;
  }

  private resolveCurrentOrgId(): number | null {
    const candidate = this.orgSettingsService.currentOrgId()
      ?? this.inMemoryCache?.organization?.id
      ?? this.inMemoryCache?.user?.currentOrganizationId;

    const numericOrgId = Number(candidate);
    if (!Number.isFinite(numericOrgId) || numericOrgId <= 0) {
      return null;
    }

    return numericOrgId;
  }

  private getSnapshot(): PermissionMatrixSnapshot | null {
    const orgId = this.getCurrentOrgIdForLookup();
    if (!orgId) {
      return null;
    }
    return this.cache.get(orgId) ?? null;
  }

  private getCurrentOrgIdForLookup(): number | null {
    return this._currentOrgId() ?? this.resolveCurrentOrgId();
  }

  private buildSnapshot(orgId: number, response: RolesPolicyResponse): PermissionMatrixSnapshot {
    const permissions = new Map<PermissionKey, PermissionMatrixEntry>();

    this.mapScope('org', response.orgRoles ?? [], response.scopes, permissions);
    this.mapScope('project', response.projectRoles ?? [], response.scopes, permissions);
    this.mapScope('card', response.cardRoles ?? [], response.scopes, permissions);

    return {
      orgId,
      permissionMatrixVersion: this.normalizeServerVersion(response.permissionMatrixVersion),
      orgAdminsImplicitProjectAccess: response.orgAdminsImplicitProjectAccess ?? false,
      orgAdminsImplicitCardAccess: response.orgAdminsImplicitCardAccess ?? false,
      permissions
    };
  }

  private mapScope(
    scope: PermissionScope,
    roles: string[],
    scopes: RolesPolicyScope[] | undefined,
    permissions: Map<PermissionKey, PermissionMatrixEntry>
  ): void {
    if (!roles.length || !scopes?.length) {
      return;
    }

    const scopeLabel = scope === 'org' ? 'Org' : scope === 'project' ? 'Project' : 'Card';
    const scopeEntry = scopes.find((entry) => entry.scope === scopeLabel);
    if (!scopeEntry?.permissions?.length) {
      return;
    }

    scopeEntry.permissions.forEach((permission) => {
      const key = permission.permissionKey as PermissionKey;
      const effectiveAllowedRoles = this.normalizeAllowedRoles(
        roles,
        permission.effectiveAllowedRoles ?? permission.defaultAllowedRoles ?? []
      );

      permissions.set(key, {
        scope,
        effectiveAllowedRoles,
        roles,
        description: permission.description
      });
    });
  }

  private normalizeAllowedRoles(roles: string[], allowedRoles: string[]): string[] {
    const uniqueAllowedRoles = new Set(allowedRoles);
    return roles.filter((role) => uniqueAllowedRoles.has(role));
  }

  private shouldRevalidate(orgId: number, maxAgeMs = this.refreshIntervalMs): boolean {
    const lastFetchedAt = this.lastFetchedAt.get(orgId);
    if (!lastFetchedAt) {
      return true;
    }

    return (Date.now() - lastFetchedAt) >= maxAgeMs;
  }

  private applyResponse(orgId: number, response: RolesPolicyResponse, options: ApplyResponseOptions = {}): void {
    const forceApply = options.forceApply ?? false;
    const fetchedAt = options.fetchedAt ?? Date.now();
    const persistToStorage = options.persistToStorage ?? true;
    const responseHash = this.computeHash(response);
    const existingHash = this.responseHashes.get(orgId);
    const existingVersion = this.serverVersions.get(orgId) ?? null;
    const nextVersion = this.normalizeServerVersion(response.permissionMatrixVersion);
    const hasChanges = existingHash !== responseHash || existingVersion !== nextVersion || !this.cache.has(orgId);

    this.lastFetchedAt.set(orgId, fetchedAt);
    this.serverVersions.set(orgId, nextVersion);

    if (persistToStorage) {
      this.persistToStorage(orgId, response, responseHash, fetchedAt);
    }

    if (!forceApply && !hasChanges) {
      return;
    }

    const snapshot = this.buildSnapshot(orgId, response);
    this.cache.set(orgId, snapshot);
    this.responseHashes.set(orgId, responseHash);
    this.bumpVersion();
  }

  private hydrateFromStorage(orgId: number): boolean {
    const metadata = this.readCacheMetadata(orgId);
    if (!metadata) {
      return false;
    }

    const cacheRecord = this.readCacheRecord(metadata.storageKey);
    if (!cacheRecord || cacheRecord.orgId !== orgId) {
      this.removeStorageEntry(metadata.storageKey);
      this.removeStorageEntry(this.buildMetadataKey(orgId));
      return false;
    }

    this.applyResponse(orgId, cacheRecord.response, {
      forceApply: true,
      fetchedAt: cacheRecord.fetchedAt ?? metadata.fetchedAt,
      persistToStorage: false
    });
    return true;
  }

  private persistToStorage(orgId: number, response: RolesPolicyResponse, responseHash: string, fetchedAt: number): void {
    if (!this.canUseStorage()) {
      return;
    }

    const version = this.normalizeServerVersion(response.permissionMatrixVersion) ?? `hash-${responseHash}`;
    const storageKey = this.buildVersionedStorageKey(orgId, version);
    const metadataKey = this.buildMetadataKey(orgId);
    const previousMetadata = this.readCacheMetadata(orgId);

    if (previousMetadata?.storageKey && previousMetadata.storageKey !== storageKey) {
      this.removeStorageEntry(previousMetadata.storageKey);
    }

    const cacheRecord: PermissionMatrixCacheRecord = {
      orgId,
      version,
      fetchedAt,
      response
    };

    const metadata: PermissionMatrixCacheMetadata = {
      orgId,
      version,
      fetchedAt,
      responseHash,
      storageKey
    };

    this.writeStorageEntry(storageKey, cacheRecord);
    this.writeStorageEntry(metadataKey, metadata);
  }

  private readCacheMetadata(orgId: number): PermissionMatrixCacheMetadata | null {
    return this.readStorageEntry<PermissionMatrixCacheMetadata>(this.buildMetadataKey(orgId));
  }

  private readCacheRecord(storageKey: string): PermissionMatrixCacheRecord | null {
    return this.readStorageEntry<PermissionMatrixCacheRecord>(storageKey);
  }

  private readStorageEntry<T>(key: string): T | null {
    if (!this.canUseStorage()) {
      return null;
    }

    try {
      const rawValue = window.localStorage.getItem(key);
      return rawValue ? JSON.parse(rawValue) as T : null;
    } catch {
      return null;
    }
  }

  private writeStorageEntry(key: string, value: unknown): void {
    if (!this.canUseStorage()) {
      return;
    }

    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Ignore storage write failures so permission checks remain non-blocking.
    }
  }

  private removeStorageEntry(key: string): void {
    if (!this.canUseStorage()) {
      return;
    }

    try {
      window.localStorage.removeItem(key);
    } catch {
      // Ignore storage cleanup failures.
    }
  }

  private canUseStorage(): boolean {
    return typeof window !== 'undefined' && !!window.localStorage;
  }

  private buildMetadataKey(orgId: number): string {
    return `${this.storageKeyPrefix}${orgId}${this.storageMetaSuffix}`;
  }

  private buildVersionedStorageKey(orgId: number, version: string): string {
    return `${this.storageKeyPrefix}${orgId}:v${version}`;
  }

  private normalizeServerVersion(version: string | number | null | undefined): string | null {
    if (version === null || version === undefined || version === '') {
      return null;
    }

    return String(version);
  }

  private computeHash(value: unknown): string {
    const serializedValue = this.stableSerialize(this.normalizeForHash(value));
    let hash = 5381;

    for (let index = 0; index < serializedValue.length; index += 1) {
      hash = ((hash << 5) + hash) ^ serializedValue.charCodeAt(index);
    }

    return (hash >>> 0).toString(16);
  }

  /**
   * Canonicalize permission-matrix payloads so re-ordered arrays do not
   * produce false-positive "changes" and trigger unnecessary permission re-evaluation.
   */
  private normalizeForHash(value: unknown): unknown {
    const maybeResponse = value as RolesPolicyResponse | null;
    if (!maybeResponse || typeof maybeResponse !== 'object') {
      return value;
    }

    const sortedStrings = (items?: string[]) => [...(items ?? [])].sort((a, b) => a.localeCompare(b));
    const sortedScopes = [...(maybeResponse.scopes ?? [])]
      .map((scope) => ({
        scope: scope.scope,
        permissions: [...(scope.permissions ?? [])]
          .map((permission) => ({
            ...permission,
            defaultAllowedRoles: sortedStrings(permission.defaultAllowedRoles),
            effectiveAllowedRoles: sortedStrings(permission.effectiveAllowedRoles)
          }))
          .sort((a, b) => (a.permissionKey ?? '').localeCompare(b.permissionKey ?? ''))
      }))
      .sort((a, b) => (a.scope ?? '').localeCompare(b.scope ?? ''));

    return {
      ...maybeResponse,
      orgRoles: sortedStrings(maybeResponse.orgRoles),
      projectRoles: sortedStrings(maybeResponse.projectRoles),
      cardRoles: sortedStrings(maybeResponse.cardRoles),
      scopes: sortedScopes
    };
  }

  private stableSerialize(value: unknown): string {
    if (value === null || typeof value !== 'object') {
      return JSON.stringify(value);
    }

    if (Array.isArray(value)) {
      return `[${value.map((item) => this.stableSerialize(item)).join(',')}]`;
    }

    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${this.stableSerialize(obj[key])}`).join(',')}}`;
  }

  private bumpVersion(): void {
    this._version.update((value) => value + 1);
  }
}
